import { forwardRef, useId, useRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'
import { useCaptionLabel } from './useCaptionLabel'

function renderLabel(text: string) {
  const trimmed = text.trimEnd()
  if (!trimmed.endsWith('*')) return text
  return (
    <>
      {trimmed.slice(0, -1).trimEnd()}
      <span className="ml-0.5 text-red-500">*</span>
    </>
  )
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: ReactNode
  rightElement?: ReactNode
  /** Matches DatePicker / DropdownSelect, so an Input standing beside one in
   *  a filter row is the same height. `md` (h-9) is the form default and what
   *  every existing caller gets. */
  size?: 'sm' | 'md'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, rightElement, size = 'md', className, id, ...props }, ref) => {
    const autoId = useId()
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-') ?? autoId
    const required = props.required || label?.trimEnd().endsWith('*')
    const innerRef = useRef<HTMLInputElement | null>(null)
    useCaptionLabel(innerRef)
    const attachRef = (node: HTMLInputElement | null) => {
      innerRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
            {renderLabel(label)}
          </label>
        )}
        <div className="relative flex items-center">
          {leftIcon && (
            <span className="pointer-events-none absolute left-3 flex items-center text-gray-400">
              {leftIcon}
            </span>
          )}
          <input
            ref={attachRef}
            id={inputId}
            className={cn(
              'block w-full rounded-lg border bg-white text-gray-900',
              // UX-12: 16px below sm so iOS does not zoom the page on focus.
              // Typed text stays 16px on phones (iOS zooms below that); the
              // placeholder matches the desktop size.
              size === 'sm'
                ? 'h-8 px-3 text-base sm:text-xs placeholder:text-xs'
                : 'h-9 px-3.5 text-base sm:text-sm placeholder:text-sm',
              'placeholder:text-gray-400 transition-shadow duration-150',
              'focus:outline-none focus:border-brand-orange focus:shadow-input-focus',
              error
                ? 'border-red-400 focus:border-red-400 focus:shadow-none'
                : 'border-gray-200 hover:border-gray-300',
              leftIcon && 'pl-9',
              rightElement && 'pr-10',
              className,
            )}
            aria-invalid={!!error}
            aria-required={required || undefined}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />
          {rightElement && (
            <span className="absolute right-3 flex items-center">{rightElement}</span>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-red-600" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-xs text-gray-500">
            {hint}
          </p>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'
